/**
 * Campaign status display style.
 *
 * Historically this was a user-toggleable /settings preference (icon vs.
 * GGC V2. design-guide dot badge), persisted to localStorage. The dot style
 * was standardized as the only look and the toggle was removed from
 * /settings — this now just exposes the fixed value via the same hook shape
 * so all existing call sites keep working unchanged.
 */

export type CampaignStatusStyle = 'icon' | 'dot';

export const DEFAULT_CAMPAIGN_STATUS_STYLE: CampaignStatusStyle = 'dot';

/** Always returns the fixed style — kept as a hook for call-site compatibility. */
export function useCampaignStatusStyle(): CampaignStatusStyle {
  return DEFAULT_CAMPAIGN_STATUS_STYLE;
}

/* ── Status mappings (raw → GgcBadge) ───────────────────────────────────*/

/**
 * Maps raw campaign status strings (underscore) to GgcBadge status props
 * (dash). The `trashed` status maps to `aborted` (closest visual match).
 */
export const CAMPAIGN_STATUS_TO_BADGE: Record<string, string> = {
  draft: 'draft',
  in_progress: 'in-progress',
  completed: 'completed',
  private_draft: 'private-draft',
  template: 'template',
  roster: 'roster',
  trashed: 'aborted',
};

/**
 * GGC design-token Tailwind bg class for the small inline dot shown before
 * a campaign name when using the dot style. Mirrors GgcBadge's dotColorMap.
 */
/**
 * `draft`/`in_progress`/`private_draft` also get `ggc-status-dot--live` (blink
 * animation, defined in components.css) — these are the statuses with ongoing
 * background work (candidate search, crawling, etc.) even before/while the
 * campaign is actually running, so the dot should read as "active" rather
 * than a static color like `completed`/`template`/`roster`/`trashed`.
 */
export const CAMPAIGN_STATUS_DOT_CLASS: Record<string, string> = {
  draft: 'bg-ggc-orange ggc-status-dot--live',
  in_progress: 'bg-ggc-blue ggc-status-dot--live',
  completed: 'bg-ggc-green',
  private_draft: 'bg-ggc-purple ggc-status-dot--live',
  template: 'bg-ggc-sub-yellow',
  roster: 'bg-ggc-sub-skyblue',
  trashed: 'bg-ggc-sub-pink',
};

/**
 * GGC design-token color (CSS var) for contexts that need a raw color value
 * instead of a Tailwind bg class — e.g. a card's left border. Mirrors
 * CAMPAIGN_STATUS_DOT_CLASS / GgcBadge so every status color stays unified
 * (do NOT use the legacy material CAMPAIGN_STATUS_COLOR for GGC surfaces).
 */
export const CAMPAIGN_STATUS_DOT_COLOR: Record<string, string> = {
  draft: 'var(--ggc-orange)',
  in_progress: 'var(--ggc-blue)',
  completed: 'var(--ggc-green)',
  private_draft: 'var(--ggc-purple)',
  template: 'var(--ggc-sub-yellow)',
  roster: 'var(--ggc-sub-skyblue)',
  trashed: 'var(--ggc-sub-pink)',
};
